// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Trainingspflanze (Skill §6 + §12 + Anforderung D7): ein Wesen, das GEZOGEN
// wird — jetzt in 20 Mini-Leveln statt 6 Stufen. Jedes Level lässt genau EIN
// Element wachsen (Blatt, Trieb, Grashalm, Besucher, Beere …); die Kurve ist
// früh dicht (Level 1–5 je Einheit) und wird später weiter (2er/3er/5er/10er).
// Choreografie nach §12: Draw-on für Stängel, Entfaltung für Blätter, Pop für
// Tierchen; Stage-Up = Squash-Dip + 8-Teilchen-Burst am neuen Element.
// Sichtbarkeit läuft über bedingtes Rendern, die Animationen über die Klassen
// .p-anim-mount (alles gestaffelt) und .p-anim-up + .p-new (nur das Neue).
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import type { CSSProperties, ReactNode } from "react"
import "@/styles/pflanze.css"

/** Level-Kurve: at = Einheiten-Schwelle, name = was wächst, burst = Austriebspunkt. */
export const LEVELS: Array<{ at: number; name: string; burst: [number, number] }> = [
  { at: 1, name: "Der Keimling bricht durch", burst: [80, 108] },
  { at: 2, name: "Erstes Keimblatt", burst: [80, 99] },
  { at: 3, name: "Zweites Keimblatt", burst: [80, 99] },
  { at: 4, name: "Ein Grashalm", burst: [53, 112] },
  { at: 5, name: "Der Stiel streckt sich", burst: [80, 85] },
  { at: 7, name: "Großes Blatt links", burst: [72, 88] },
  { at: 9, name: "Großes Blatt rechts", burst: [88, 78] },
  { at: 11, name: "Noch ein Grashalm", burst: [108, 113] },
  { at: 14, name: "Seitentrieb links", burst: [64, 100] },
  { at: 17, name: "Seitentrieb rechts", burst: [96, 100] },
  { at: 20, name: "Ein Glühwürmchen schaut vorbei", burst: [44, 103] },
  { at: 25, name: "Der Stiel wächst hoch", burst: [80, 55] },
  { at: 30, name: "Kronblatt links", burst: [74, 60] },
  { at: 35, name: "Kronblatt rechts", burst: [86, 52] },
  { at: 40, name: "Eine Knospe!", burst: [80, 40] },
  { at: 45, name: "Ein Schmetterling besucht", burst: [116, 38] },
  { at: 50, name: "Die Blüte öffnet sich", burst: [80, 31] },
  { at: 60, name: "Erste Beere", burst: [50, 84] },
  { at: 75, name: "Zweite Beere", burst: [110, 84] },
  { at: 100, name: "Glühwürmchen-Abend", burst: [80, 55] },
]

/** Level 0..20 aus absolvierten Einheiten — wächst nur, schrumpft nie. */
export function plantLevel(units: number): number {
  return LEVELS.filter((l) => units >= l.at).length
}

const MAX = LEVELS.length

function captions(level: number, units: number): [string, string] {
  if (level === 0) return ["Samenkorn", "deine erste Einheit weckt es"]
  const name = LEVELS[level - 1].name
  if (level >= MAX) return [`Level ${MAX} · ${name}`, `${LEVELS[MAX - 1].at}+ Einheiten · voll erblüht`]
  const rest = LEVELS[level].at - units
  return [`Level ${level} · ${name}`, `noch ${rest} ${rest === 1 ? "Einheit" : "Einheiten"} bis Level ${level + 1}`]
}

/* jsdom-sicher: fehlt matchMedia, gilt reduzierte Bewegung (Endzustand sofort) */
function reducedMotion(): boolean {
  return (
    typeof window.matchMedia !== "function" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/* Blatt-Ansatzpunkt als transform-origin (§12 A.2) + Stagger-Index (90ms) */
function leafStyle(lf: number, jx: number, jy: number): CSSProperties {
  return { "--lf": lf, "--jx": `${jx}px`, "--jy": `${jy}px` } as CSSProperties
}

/** `ruhig`: nur das Bild, kein Aufbau und kein Feiern. Für die Startseite —
 *  gefeiert wird im Fortschritt, sonst nutzt sich der Moment ab (Skill §9). */
export default function Trainingspflanze({ units, ruhig = false }: { units: number; ruhig?: boolean }) {
  const level = plantLevel(units)
  const [cap1, cap2] = captions(level, units)
  const [anim, setAnim] = useState<"mount" | "up" | null>(() =>
    reducedMotion() || ruhig ? null : "mount",
  )
  const [prevCaption, setPrevCaption] = useState<string | null>(null)
  const [newLevels, setNewLevels] = useState<ReadonlySet<number>>(new Set())
  const vorher = useRef<{ level: number; cap: string } | null>(null)

  // Layout-Effekt: die Anim-Klasse muss VOR dem ersten Paint des neuen Levels
  // sitzen, sonst blitzt der Endzustand einen Frame lang auf.
  useLayoutEffect(() => {
    const alt = vorher.current
    vorher.current = { level, cap: cap1 }
    // Nur echte Level-UPS feiern — runter gibt es nicht zu animieren (§12 D).
    if (alt === null || level <= alt.level || reducedMotion() || ruhig) return
    const frisch = new Set<number>()
    for (let n = alt.level + 1; n <= level; n++) frisch.add(n)
    setNewLevels(frisch)
    setAnim("up")
    setPrevCaption(alt.cap)
  }, [level, cap1, ruhig])

  // Aufräumen nach der Choreografie (Level-Up ≤1,2s; Mount gestaffelt ~1,8s)
  useEffect(() => {
    if (anim === null) return
    const t = setTimeout(
      () => {
        setAnim(null)
        setPrevCaption(null)
        setNewLevels(new Set())
      },
      anim === "mount" ? 2000 : 1400,
    )
    return () => clearTimeout(t)
  }, [anim])

  /* Ein Level-Element: gerendert ab Level n (optional bis until, z. B. Knospe
     → Blüte). --pd staffelt den Mount (80ms je Level, Deckel 10) bzw. den
     Level-Up (120ms + 200ms je zusätzlich übersprungenem Level). */
  const el = (n: number, node: ReactNode, until?: number) => {
    if (level < n || (until !== undefined && level >= until)) return null
    const neu = newLevels.has(n)
    const pd =
      anim === "up"
        ? 120 + [...newLevels].filter((m) => m < n).length * 200
        : Math.min(n, 10) * 80
    return (
      <g className={`p-part${neu ? " p-new" : ""}`} style={{ "--pd": `${pd}ms` } as CSSProperties}>
        {node}
      </g>
    )
  }

  // Burst am höchsten neuen Element (bei mehreren Level-Sprüngen auf einmal)
  const burstLevel = newLevels.size ? Math.max(...newLevels) : level
  const [bx, by] = LEVELS[Math.max(0, Math.min(burstLevel, MAX) - 1)]?.burst ?? [80, 108]

  return (
    <div className={`pflanze p-lvl-${level}${anim ? ` p-anim-${anim}` : ""}`}>
      <svg viewBox="0 0 160 140" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g
          fill="none"
          stroke="var(--tanne)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {/* Erdhügel mit Steinchen — settelt beim Austrieb */}
          <g className="p-mound">
            <path d="M40 120 C54 106 106 106 120 120 Z" fill="var(--sand)" />
            <circle cx="46" cy="124" r="2" stroke="var(--moos)" />
            <circle cx="116" cy="125" r="2.5" stroke="var(--moos)" />
          </g>

          {/* Grashalme: Level 4 + 8 (Draw-on wie Stängel) */}
          {el(4, <path className="p-stem" pathLength={1} d="M52 117 C49 111 50 105 54 100" stroke="var(--moos)" />)}
          {el(8, <path className="p-stem" pathLength={1} d="M108 118 C111 113 110 108 107 104" stroke="var(--moos)" />)}

          {/* Ganze Pflanze: Ziel des Anticipation-Squash (Origin Fußpunkt) */}
          <g className="p-plant">
            {/* Level 0: Samenkorn in der Erde (verschwindet, sobald es keimt) */}
            {level === 0 && (
              <g className="p-part">
                <g className="p-seed">
                  <ellipse cx="80" cy="112" rx="5" ry="3.5" fill="var(--creme)" />
                  <path d="M78 112 C79 110.5 81 110.5 82 112" strokeWidth="1.5" />
                </g>
              </g>
            )}

            {/* Seitentriebe (Level 9/10) + Beeren (Level 18/19) */}
            <g className="p-sway p-sway--b">
              {el(9, (
                <>
                  <path
                    className="p-stem"
                    pathLength={1}
                    d="M78 114 C70 110 62 103 58 94"
                    stroke="var(--moos)"
                    strokeWidth="2.5"
                  />
                  <g className="p-leaf" style={leafStyle(0, 68, 107)}>
                    <g className="p-leafsway">
                      <path d="M68 107 C62 107 56 104 53 99 C59 97 65 101 68 107 Z" fill="var(--moos)" />
                    </g>
                  </g>
                  <g className="p-leaf" style={leafStyle(2, 58, 94)}>
                    <g className="p-leafsway">
                      <path d="M58 94 C50 92 44 85 43 77 C52 80 57 87 58 94 Z" fill="var(--salbei)" />
                      <path d="M56 91 C51 88 47 84 45 80" stroke="var(--creme)" strokeWidth="1.5" />
                    </g>
                  </g>
                </>
              ))}
              {el(18, (
                <g className="p-leaf" style={leafStyle(0, 50, 88)}>
                  <g className="p-leafsway">
                    <circle cx="49" cy="84" r="3.2" fill="var(--sand)" />
                    <path d="M49 81 C49 79.5 50 78.5 51 78" strokeWidth="1.5" />
                  </g>
                </g>
              ))}
            </g>
            <g className="p-sway p-sway--c">
              {el(10, (
                <>
                  <path
                    className="p-stem"
                    pathLength={1}
                    d="M82 114 C90 110 98 103 102 94"
                    stroke="var(--moos)"
                    strokeWidth="2.5"
                  />
                  <g className="p-leaf" style={leafStyle(1, 92, 107)}>
                    <g className="p-leafsway">
                      <path d="M92 107 C98 107 104 104 107 99 C101 97 95 101 92 107 Z" fill="var(--salbei)" />
                    </g>
                  </g>
                  <g className="p-leaf" style={leafStyle(3, 102, 94)}>
                    <g className="p-leafsway">
                      <path d="M102 94 C110 92 116 85 117 77 C108 80 103 87 102 94 Z" fill="var(--moos)" />
                      <path d="M104 91 C109 88 113 84 115 80" stroke="var(--creme)" strokeWidth="1.5" />
                    </g>
                  </g>
                </>
              ))}
              {el(19, (
                <g className="p-leaf" style={leafStyle(1, 110, 88)}>
                  <g className="p-leafsway">
                    <circle cx="111" cy="84" r="3.2" fill="var(--sand)" />
                    <path d="M111 81 C111 79.5 110 78.5 109 78" strokeWidth="1.5" />
                  </g>
                </g>
              ))}
            </g>

            {/* Hauptstiel: eigene Wiege-Gruppe (Periode 7s) */}
            <g className="p-sway p-sway--a">
              {/* Level 1–3: Keimling + Keimblatt-Paar */}
              {el(1, (
                <path
                  className="p-stem"
                  pathLength={1}
                  d="M80 116 C81 110 78 104 80 97"
                  stroke="var(--moos)"
                  strokeWidth="3"
                />
              ))}
              {el(2, (
                <g className="p-leaf" style={leafStyle(0, 80, 99)}>
                  <g className="p-leafsway">
                    <path d="M80 99 C73 98 68 93 68 86 C75 88 79 93 80 99 Z" fill="var(--moos)" />
                  </g>
                </g>
              ))}
              {el(3, (
                <g className="p-leaf" style={leafStyle(1, 80, 99)}>
                  <g className="p-leafsway">
                    <path d="M80 99 C87 98 92 93 92 87 C85 89 81 94 80 99 Z" fill="var(--salbei)" />
                  </g>
                </g>
              ))}

              {/* Level 5–7: Stiel wächst, großes Blattpaar mit Adern */}
              {el(5, (
                <path
                  className="p-stem"
                  pathLength={1}
                  d="M80 98 C82 90 79 79 80 70"
                  stroke="var(--moos)"
                  strokeWidth="3"
                />
              ))}
              {el(6, (
                <g className="p-leaf" style={leafStyle(0, 80, 90)}>
                  <g className="p-leafsway">
                    <path d="M80 90 C68 90 59 83 57 72 C69 73 78 80 80 90 Z" fill="var(--moos)" />
                    <path d="M78 88 C71 84 65 79 61 75" stroke="var(--creme)" strokeWidth="1.5" />
                  </g>
                </g>
              ))}
              {el(7, (
                <g className="p-leaf" style={leafStyle(1, 80, 80)}>
                  <g className="p-leafsway">
                    <path d="M80 80 C92 80 101 73 103 62 C91 63 82 70 80 80 Z" fill="var(--salbei)" />
                    <path d="M82 78 C89 74 95 69 99 65" stroke="var(--creme)" strokeWidth="1.5" />
                  </g>
                </g>
              ))}

              {/* Level 12–14: kräftig — Stiel bis oben, Kronblatt-Paar */}
              {el(12, (
                <path
                  className="p-stem"
                  pathLength={1}
                  d="M80 70 C78 62 82 53 80 44"
                  stroke="var(--moos)"
                  strokeWidth="3"
                />
              ))}
              {el(13, (
                <g className="p-leaf" style={leafStyle(0, 80, 64)}>
                  <g className="p-leafsway">
                    <path d="M80 64 C70 62 63 56 61 47 C71 49 78 55 80 64 Z" fill="var(--salbei)" />
                    <path d="M78 61 C72 57 67 53 64 50" stroke="var(--creme)" strokeWidth="1.5" />
                  </g>
                </g>
              ))}
              {el(14, (
                <g className="p-leaf" style={leafStyle(1, 80, 56)}>
                  <g className="p-leafsway">
                    <path d="M80 56 C90 54 97 48 99 39 C89 41 82 48 80 56 Z" fill="var(--moos)" />
                    <path d="M82 53 C88 49 93 45 96 42" stroke="var(--creme)" strokeWidth="1.5" />
                  </g>
                </g>
              ))}

              {/* Level 15: Knospe — öffnet sich bei Level 17 zur Blüte */}
              {el(15, (
                <g className="p-leaf" style={leafStyle(2, 80, 46)}>
                  <g className="p-leafsway">
                    <path d="M80 46 C74 40 74 31 80 26 C86 31 86 40 80 46 Z" fill="var(--salbei)" />
                    <path d="M80 43 C77 39 77 33 80 29" stroke="var(--creme)" strokeWidth="1.5" />
                  </g>
                </g>
              ), 17)}

              {/* Level 17: Creme-Blüte, atmet im Idle */}
              {el(17, (
                <g className="p-leaf" style={leafStyle(0, 80, 44)}>
                  <g className="p-breathe">
                    <circle cx="80" cy="19" r="7" fill="var(--creme)" />
                    <circle cx="91.5" cy="27" r="7" fill="var(--creme)" />
                    <circle cx="87" cy="40.5" r="7" fill="var(--creme)" />
                    <circle cx="73" cy="40.5" r="7" fill="var(--creme)" />
                    <circle cx="68.5" cy="27" r="7" fill="var(--creme)" />
                    <circle cx="80" cy="31" r="5" fill="var(--sand)" />
                  </g>
                </g>
              ))}
            </g>

            {/* Besucher-Tierchen (poppen, wiegen nicht mit dem Wind) */}
            {el(11, (
              <g className="p-crit p-firefly" style={{ "--fd": "0s" } as CSSProperties}>
                <circle cx="44" cy="103" r="2.6" fill="var(--creme)" stroke="var(--moos)" strokeWidth="1.5" />
                <path d="M41.5 100.5 C40 99 40 97.5 41 96.5 M46.5 100.5 C48 99 48 97.5 47 96.5" strokeWidth="1.2" stroke="var(--moos)" />
              </g>
            ))}
            {el(16, (
              <g className="p-crit p-flutter">
                <path d="M116 38 C112 33 105 33 104 38 C105 43 112 43 116 38 Z" fill="var(--salbei)" />
                <path d="M116 38 C120 33 127 33 128 38 C127 43 120 43 116 38 Z" fill="var(--moos)" />
                <path d="M116 35 V41" strokeWidth="1.5" />
              </g>
            ))}
            {el(20, (
              <g className="p-crit">
                <circle className="p-glow" style={{ "--fd": "-1.3s" } as CSSProperties} cx="58" cy="52" r="2.2" fill="var(--creme)" stroke="var(--moos)" strokeWidth="1.2" />
                <circle className="p-glow" style={{ "--fd": "-2.8s" } as CSSProperties} cx="104" cy="60" r="2.2" fill="var(--creme)" stroke="var(--moos)" strokeWidth="1.2" />
              </g>
            ))}
          </g>

          {/* 8-Teilchen-Burst am Austriebspunkt — nur bei echtem Level-Up */}
          {anim === "up" && (
            <g key={level} className="p-burst" transform={`translate(${bx} ${by})`}>
              {Array.from({ length: 8 }, (_, i) => (
                <circle
                  key={i}
                  r={2 + ((i * 13) % 3) / 2}
                  style={
                    { "--i": i, "--ty": `${-(18 + ((i * 37) % 11))}px` } as CSSProperties
                  }
                />
              ))}
            </g>
          )}
        </g>
      </svg>
      <div className="p-captions">
        {prevCaption !== null && (
          <p className="p-caption p-caption--out" aria-hidden="true">
            {prevCaption}
          </p>
        )}
        <p key={level} className="p-caption">
          {cap1}
        </p>
        <p className="p-caption p-caption--sub">{cap2}</p>
      </div>
    </div>
  )
}
